import {
  selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn,
  selectIsDisplayFinishedCampaignSubfoldersFeatureFlagOn,
} from 'core/userContext';
import { useListNextBestActionEligibleCampaigns } from 'modules/ai/nba/hooks/useNBAEligibleCampaignQuery';
import { useProjectIdSearchParamOnMount } from 'modules/app/hooks/useInitializeCurrentProject/useProjectIdSearchParamOnMount';
import { RECENT_ALERTS_DURATION_WINDOW } from 'modules/campaigns/lib/constants';
import { useCampaignsHasRecentTriggeredAlerts } from 'modules/campaigns/lib/hooks';
import { FolderPageNamespace, useStoreLastVisitedFolderId } from 'modules/foldering';
import { GRAPHQL_TYPENAMES } from 'modules/graphql/types';
import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { usePaginationContext } from 'utils/contexts/pagination';
import { useSortingContext } from 'utils/contexts/sorting';
import {
  serializationOptionsForNumberValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import noop from 'utils/noop';
import { CAMPAIGN_STATE_FILTERS, FOLDER_ID_SEARCH_PARAM } from '../../constants';
import { ContentFilter, useCampaignFolders } from '../../hooks/useCampaignFolders';
import { useCampaignProject } from '../../hooks/useCampaignProject';
import { useParentCampaign } from '../../hooks/useParentCampaign';
import { useRootFolderId } from '../../hooks/useRootFolderId';
import { checkArrayLength, getAlertsFilterInfo, replacePseudoStatus } from '../../utils';
import { useCampaignFiltersContext } from './CampaignFiltersContext';
import { CampaignQueryContext, CampaignQueryContextState } from './CampaignQueryContext';

export const CampaignQueryContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  // General GQL queries
  const rootFolderId = useRootFolderId();
  const { campaignProject, isLoading: isLoadingProject } = useCampaignProject();

  // AI queries
  const { data: nbaEnabledCampaignData } = useListNextBestActionEligibleCampaigns();

  // URL Params
  useProjectIdSearchParamOnMount();
  const [folderId, setFolderId] = useSearchParamState({
    searchParamKey: FOLDER_ID_SEARCH_PARAM,
    stateValueWhenSearchParamIsUnset: rootFolderId,
    ...serializationOptionsForNumberValue,
  });

  // Contexts
  const { currentPage, pageSize } = usePaginationContext();
  const { sortBy, sortDirection } = useSortingContext();
  const {
    alerts,
    campaignTypes,
    channelTypes,
    creators,
    labels,
    lists,
    media,
    modifiers,
    parentCampaignId,
    placements,
    stateCategory,
    states,
    searchQuery,
    templateType,
    templateStatus,
    templateQualityRating,
    hideFolders,
  } = useCampaignFiltersContext();

  const isDisplayFinishedCampaignSubfoldersEnabled = useSelector(
    selectIsDisplayFinishedCampaignSubfoldersFeatureFlagOn,
  );

  const groupRecurringCampaigns = campaignProject?.groupRecurringCampaigns ?? false;
  const isViewingChildCampaigns = groupRecurringCampaigns && parentCampaignId;
  const isInRootFolder = folderId === rootFolderId;
  const isFinished = stateCategory === CAMPAIGN_STATE_FILTERS[4].id;
  const queryFolderId = isViewingChildCampaigns || isInRootFolder ? null : folderId;

  const shouldHideFoldersForFinishedCampaigns =
    !isDisplayFinishedCampaignSubfoldersEnabled && isFinished;
  const isContentFilter =
    hideFolders || shouldHideFoldersForFinishedCampaigns || isViewingChildCampaigns;

  useStoreLastVisitedFolderId(FolderPageNamespace.CAMPAIGN_INDEX, folderId, isInRootFolder);

  // GraphQL

  // GraphQL queries rely on the pagination and sort data for each query, we need a helper with access to the local scope
  const queryVariables = useMemo(() => {
    const contentFilter = isContentFilter ? { content: [ContentFilter.ContentItem] } : {};

    const additionalFilters: Record<string, any> = {};
    if (groupRecurringCampaigns) {
      // Only filter by parent Campaign if groupRecurringCampaigns is true
      if (parentCampaignId) {
        additionalFilters.recurringCampaignIds = [parentCampaignId];
      } else {
        additionalFilters.parentsOnly = true;
      }
    }

    const campaignAlertsFilterInfo = getAlertsFilterInfo(alerts);

    return {
      campaignFilterInfo: {
        campaignAlertsFilterInfo,
        labelIds: checkArrayLength(labels),
        messageMedium: checkArrayLength(media),
        campaignType: checkArrayLength(campaignTypes),
        channelType: checkArrayLength(channelTypes),
        listIds: checkArrayLength(lists),
        campaignState: checkArrayLength(states) || CAMPAIGN_STATE_FILTERS[0].query,
        createdByUserId: checkArrayLength(creators),
        updatedByUserId: checkArrayLength(modifiers),
        placementIds: checkArrayLength(placements),
        templateType: checkArrayLength(templateType),
        whatsAppTemplateStatus: replacePseudoStatus(templateStatus),
        whatsAppTemplateQuality: checkArrayLength(templateQualityRating),
        ...additionalFilters,
      },
      folderId: queryFolderId,
      recursive: searchQuery.length > 0,
      pagination: {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      },
      search: searchQuery || null, // this might move from this location
      sort: {
        sortBy,
        sortDirection,
      },
      ...contentFilter,
    };
  }, [
    // Campaign-related variables
    alerts,
    campaignTypes,
    channelTypes,
    creators,
    queryFolderId,
    labels,
    lists,
    media,
    modifiers,
    parentCampaignId,
    placements,
    searchQuery,
    states,
    templateType,
    templateStatus,
    templateQualityRating,
    groupRecurringCampaigns,
    isContentFilter,
    // Page-related variables
    currentPage,
    pageSize,
    sortBy,
    sortDirection,
  ]);

  // Main query for the campaigns
  const {
    childFolders = [],
    childCampaigns = [],
    error = false,
    isLoading = false,
    folderData = {
      ancestors: [],
      content: [],
      id: rootFolderId,
      name: '',
      isFavorite: false,
      __typename: GRAPHQL_TYPENAMES.CAMPAIGN_FOLDER,
    },
    paginationInfo = {
      count: 0,
      limit: 20,
      offset: 0,
      page: 1,
      __typename: GRAPHQL_TYPENAMES.INFO,
    },
    refetch = noop,
  } = useCampaignFolders({ variables: queryVariables, skip: isLoadingProject });

  // Helper query to get the parent campaign for a recurring campaign
  const {
    fetch: fetchParent,
    parentCampaign,
    isLoading: isLoadingParent,
    error: parentHasError,
  } = useParentCampaign(parentCampaignId);

  // Function to refetch queries
  const refetchCampaigns = () => {
    refetch(queryVariables);
    if (parentCampaignId) {
      fetchParent(parentCampaignId);
    }
  };

  const isCampaignPerformanceAlertsHistoryEnabled = useSelector(
    selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn,
  );
  const { campaignTriggeredAlerts } = useCampaignsHasRecentTriggeredAlerts({
    variables: {
      alertTimeWindow: RECENT_ALERTS_DURATION_WINDOW,
      campaignIds: childCampaigns?.map((campaign) => campaign?.id),
    },
    skip: !isCampaignPerformanceAlertsHistoryEnabled,
  });

  const passedParentCampaign = parentCampaignId === parentCampaign?.id ? parentCampaign : undefined;

  /**
   * When in the recurring campaign parent/child view, we are
   * fetching the children from the root-level folder and
   * mixing that data in with the parent campaign (wherever it may
   * actually be located) and displaying everything all together
   * in the table. This means the pagination info we receive does
   * not include the parent campaign in the count so we have to
   * manually add 1 in those cases so the pagination and total
   * results for the table are ultimately rendered correctly.
   */
  const updatedPaginationInfo = passedParentCampaign
    ? { ...paginationInfo, count: paginationInfo.count + 1 }
    : paginationInfo;

  const context: CampaignQueryContextState = {
    searchQuery,
    folderData,
    folderId,
    folders: passedParentCampaign ? [] : childFolders,
    groupRecurringCampaigns,
    hasResponseError: error || parentHasError,
    isLoading: isLoadingProject || isLoading || isLoadingParent,
    paginationInfo: updatedPaginationInfo,
    parentCampaign: passedParentCampaign,
    refetch: refetchCampaigns,
    rootFolderId,
    setFolderId,
    campaigns: childCampaigns,
    nbaEnabledCampaignIds:
      nbaEnabledCampaignData?.listNextBestActionEligibleCampaigns?.campaignIds || [],
    campaignTriggeredAlerts,
  };

  return <CampaignQueryContext.Provider value={context}>{children}</CampaignQueryContext.Provider>;
};
